import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Save, Download, Upload, AlertTriangle } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { getSettings, saveSettings } from "@/lib/api";
import { exportBackup, importBackup } from "@/lib/api";

export default function Settings() {
  const [form, setForm] = useState({
    companyName: "", address: "", phone: "",
    conversionFactor: 1, rate: 25, serviceCharges: 50,
  });
  const [confirmImport, setConfirmImport] = useState(false);
  const [importData, setImportData] = useState(null);

  useEffect(() => { getSettings().then(setForm); }, []);

  const handleSave = async () => {
    await saveSettings(form);
    toast.success("Settings saved successfully");
  };

  const updateField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleExport = async () => {
    const data = await exportBackup();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `superflame_backup_${new Date().toISOString().split("T")[0]}.json`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success("Backup exported successfully");
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        if (!parsed.customers && !parsed.bills && !parsed.settings && !parsed.sf_customers && !parsed.sf_bills && !parsed.sf_settings) {
          toast.error("Invalid backup file format");
          return;
        }
        setImportData(parsed);
        setConfirmImport(true);
      } catch {
        toast.error("Failed to parse backup file");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleImportConfirm = async () => {
    if (!importData) return;
    const payload = {
      customers: importData.customers || importData.sf_customers || [],
      bills: importData.bills || importData.sf_bills || [],
      settings: importData.settings || importData.sf_settings || null,
    };
    await importBackup(payload);
    if (payload.settings) setForm(payload.settings);
    setConfirmImport(false);
    setImportData(null);
    toast.success(`Data restored successfully! ${payload.customers.length} customers, ${payload.bills.length} bills.`);
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[800px] mx-auto" data-testid="settings-page">
      <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-black text-zinc-900 mb-8">
        Settings
      </h1>

      <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
        <CardHeader>
          <CardTitle className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
            Company Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Company Name</Label>
            <Input data-testid="settings-company-name" value={form.companyName} onChange={(e) => updateField("companyName", e.target.value)} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
          </div>
          <div>
            <Label>Address</Label>
            <Input data-testid="settings-address" value={form.address} onChange={(e) => updateField("address", e.target.value)} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input data-testid="settings-phone" value={form.phone} onChange={(e) => updateField("phone", e.target.value)} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
          </div>
          <div>
            <Label>UPI ID (for QR on invoices)</Label>
            <Input data-testid="settings-upi-id" value={form.upiId || ""} onChange={(e) => updateField("upiId", e.target.value)} placeholder="e.g. yourname@paytm" className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
          </div>
        </CardContent>
      </Card>

      <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none mt-6">
        <CardHeader>
          <CardTitle className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
            Default Billing Parameters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label>Conversion Factor</Label>
              <Input data-testid="settings-conversion-factor" type="number" step="0.01" value={form.conversionFactor} onChange={(e) => updateField("conversionFactor", parseFloat(e.target.value) || 0)} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
            </div>
            <div>
              <Label>Rate (per unit)</Label>
              <Input data-testid="settings-rate" type="number" step="0.01" value={form.rate} onChange={(e) => updateField("rate", parseFloat(e.target.value) || 0)} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
            </div>
            <div>
              <Label>Service Charges</Label>
              <Input data-testid="settings-service-charges" type="number" step="0.01" value={form.serviceCharges} onChange={(e) => updateField("serviceCharges", parseFloat(e.target.value) || 0)} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
            </div>
          </div>
          <Separator className="my-4" />
          <Button data-testid="save-settings-button" onClick={handleSave} className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm">
            <Save className="w-4 h-4 mr-2" /> Save Settings
          </Button>
        </CardContent>
      </Card>

      <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none mt-6">
        <CardHeader>
          <CardTitle className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
            Data Backup & Restore
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <p className="text-sm text-zinc-600 mb-3">
              Export all your data (customers, bills, settings) as a JSON file for safekeeping.
            </p>
            <Button data-testid="export-backup-button" variant="outline" onClick={handleExport} className="rounded-sm border-zinc-300">
              <Download className="w-4 h-4 mr-2" /> Export Backup
            </Button>
          </div>
          <Separator />
          <div>
            <p className="text-sm text-zinc-600 mb-1">
              Restore data from a previously exported backup file.
            </p>
            <p className="text-xs text-red-500 mb-3 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> This will overwrite all existing data.
            </p>
            <label
              data-testid="import-backup-label"
              className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-300 rounded-sm text-sm cursor-pointer hover:bg-zinc-50 transition-colors"
            >
              <Upload className="w-4 h-4" /> Import Backup
              <input type="file" accept=".json" onChange={handleFileSelect} className="hidden" />
            </label>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={confirmImport} onOpenChange={setConfirmImport}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore Data from Backup?</AlertDialogTitle>
            <AlertDialogDescription>
              This will replace all existing data with the backup file contents:
              {importData && (
                <span className="block mt-2 font-mono text-sm">
                  {importData.customers?.length || importData.sf_customers?.length || 0} customers, {importData.bills?.length || importData.sf_bills?.length || 0} bills
                  {importData.exportedAt && ` (backed up ${new Date(importData.exportedAt).toLocaleDateString("en-IN")})`}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-import-button" className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-import-button"
              onClick={handleImportConfirm}
              className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm"
            >
              Restore Data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
